import {
  AudioLines,
  BookOpen,
  FileText,
  Lightbulb,
  Loader2,
  Mic,
  MoreHorizontal,
  Zap,
} from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { WhiteboardPracticeSelectionNotice } from "../transcription-view/WhiteboardPracticeSelectionNotice";
import { BridgeMark } from "../ui/BridgeMark";

interface PlusOptionsMenuProps {
  whiteboardSelectionId?: number;
  onSummary?: () => void;
  isSummarizing?: boolean;
  onGenerateExercise?: () => void;
  onGiveHint?: () => void;
  onViewSummary?: () => void;
  onGenerateSentenceMap?: () => void;
  onContinueSession?: () => void;
  pronunciationAnalysisAvailable?: boolean;
  pronunciationAnalysisEnabled?: boolean;
  onPronunciationAnalysisEnabledChange?: (enabled: boolean) => void;
}

export const PlusOptionsMenu: React.FC<PlusOptionsMenuProps> = ({
  whiteboardSelectionId,
  onSummary,
  isSummarizing = false,
  onGenerateExercise,
  onGiveHint,
  onViewSummary,
  onGenerateSentenceMap,
  onContinueSession,
  pronunciationAnalysisAvailable = false,
  pronunciationAnalysisEnabled = true,
  onPronunciationAnalysisEnabledChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscape);
    }
    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape") setIsOpen(false);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  const toggleMenu = () => {
    if (isSummarizing) return;
    setIsOpen(!isOpen);
  };

  return (
    <div className="relative" ref={menuRef}>
      {isOpen && (
        <div
          role="menu"
          className="absolute bottom-full left-0 z-50 mb-2 w-56 rounded-md border border-white/10 bg-[#1b1c22] p-1.5 shadow-2xl shadow-black/45"
        >
          {onContinueSession && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                onContinueSession();
                setIsOpen(false);
              }}
              className="flex w-full items-center gap-3 rounded px-3 py-2.5 text-left text-sm font-semibold text-orange-200 transition-colors hover:bg-orange-400/10 hover:text-orange-100"
            >
              <Mic size={15} className="text-orange-300" />
              Continue this session
            </button>
          )}
          {onSummary && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                onSummary();
                setIsOpen(false);
              }}
              disabled={isSummarizing}
              className="flex w-full items-center gap-3 rounded px-3 py-2.5 text-left text-sm text-white/75 transition-colors hover:bg-white/[0.06] hover:text-white disabled:opacity-50"
            >
              <FileText size={15} className="text-orange-300" />
              Create summary
            </button>
          )}
          {onViewSummary && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                onViewSummary();
                setIsOpen(false);
              }}
              className="flex w-full items-center gap-3 rounded px-3 py-2.5 text-left text-sm text-white/75 transition-colors hover:bg-white/[0.06] hover:text-white"
            >
              <BookOpen size={15} className="text-white/45" />
              Last summary
            </button>
          )}
          {onGenerateExercise && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                onGenerateExercise();
                setIsOpen(false);
              }}
              className="flex w-full items-center gap-3 rounded px-3 py-2.5 text-left text-sm text-white/75 transition-colors hover:bg-white/[0.06] hover:text-white"
            >
              <Zap size={15} className="text-orange-300" />
              Exercise
            </button>
          )}
          {onGiveHint && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                onGiveHint();
                setIsOpen(false);
              }}
              className="flex w-full items-center gap-3 rounded px-3 py-2.5 text-left text-sm text-white/75 transition-colors hover:bg-white/[0.06] hover:text-white"
            >
              <Lightbulb size={15} className="text-white/45" />
              Hint
            </button>
          )}
          {onGenerateSentenceMap && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                onGenerateSentenceMap();
                setIsOpen(false);
              }}
              className="flex w-full items-center gap-3 rounded px-3 py-2.5 text-left text-sm text-white/75 transition-colors hover:bg-white/[0.06] hover:text-white"
            >
              <BridgeMark className="h-[15px] w-[15px] text-cyan-200" />
              SentenceBridge
            </button>
          )}
          {pronunciationAnalysisAvailable && onPronunciationAnalysisEnabledChange && (
            <button
              type="button"
              role="menuitemcheckbox"
              aria-checked={pronunciationAnalysisEnabled}
              onClick={() => onPronunciationAnalysisEnabledChange(!pronunciationAnalysisEnabled)}
              className="mt-1 flex w-full items-center gap-3 border-t border-white/10 px-3 py-2.5 text-left text-sm text-white/75 transition-colors hover:bg-white/[0.06] hover:text-white"
            >
              <AudioLines size={15} className="text-white/45" />
              <span className="min-w-0 flex-1">
                <span className="block">Pronunciation feedback</span>
                <span className="block text-[10px] leading-3.5 text-white/35">
                  Voice notes only
                </span>
              </span>
              <span
                aria-hidden="true"
                className={`relative h-5 w-9 rounded-full border transition-colors ${
                  pronunciationAnalysisEnabled
                    ? "border-orange-300/45 bg-orange-300/20"
                    : "border-white/12 bg-white/[0.05]"
                }`}
              >
                <span
                  className={`absolute top-0.5 h-3.5 w-3.5 rounded-full transition-transform ${
                    pronunciationAnalysisEnabled
                      ? "translate-x-[17px] bg-orange-200"
                      : "translate-x-0.5 bg-white/45"
                  }`}
                />
              </span>
            </button>
          )}
        </div>
      )}

      <button
        type="button"
        onClick={toggleMenu}
        disabled={isSummarizing}
        className="relative z-20 flex h-11 w-11 items-center justify-center rounded-md border border-white/10 bg-white/[0.04] text-white/55 transition-colors hover:bg-white/[0.07] hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
        title={isOpen ? "Close menu" : "More options"}
        aria-label={isOpen ? "Close writing tools" : "Open writing tools"}
        aria-expanded={isOpen}
      >
        {isSummarizing ? (
          <Loader2 size={18} className="animate-spin" />
        ) : (
          <MoreHorizontal size={19} className={isOpen ? "text-orange-300" : undefined} />
        )}
      </button>
      <WhiteboardPracticeSelectionNotice
        selectionId={whiteboardSelectionId}
        className="pointer-events-none absolute bottom-full right-0 mb-2 w-max max-w-[min(14rem,calc(100vw-2rem))]"
      />
    </div>
  );
};
